<template>
  <div id="app">
    <PrimeToast position="top-right" />
    <PrimeConfirmDialog />
    <OfflineStatusNotification />
    <div id="pwa-install-prompt" v-if="showInstallPrompt" class="pwa-install-prompt">
      <div class="pwa-prompt-content">
        <span>{{ $t('pwa.title') }}</span>
        <div class="pwa-buttons">
          <button @click="installPWA" class="install-button">{{ $t('pwa.install') }}</button>
          <button @click="dismissInstallPrompt" class="dismiss-button">{{ $t('pwa.later') }}</button>
        </div>
      </div>
    </div>
    <router-view v-slot="{ Component }">
      <component :is="Component" />
    </router-view>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted, onBeforeUnmount } from 'vue';
import { useToast } from 'primevue/usetoast';
import OfflineStatusNotification from './components/OfflineStatusNotification.vue';
import { usePrimeVueLocale } from './composables/usePrimeVueLocale';
import { initializeApiToast } from './services/api';

export default defineComponent({
  name: 'App',
  components: {
    OfflineStatusNotification
  },
  setup() {
    // Initialize PrimeVue locale management
    usePrimeVueLocale();

    // Initialize toast for API interceptor
    const toast = useToast();
    initializeApiToast(toast);
    
    const deferredPrompt = ref<any>(null);
    const showInstallPrompt = ref(false);
    
    // Handle PWA install prompt
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent Chrome 76+ from automatically showing the prompt
      e.preventDefault();
      // Store the event so it can be triggered later
      deferredPrompt.value = e;
      // Show install prompt to user
      showInstallPrompt.value = true;
    };
    
    // Handle app installed event
    const handleAppInstalled = () => {
      // Clear the deferredPrompt
      deferredPrompt.value = null;
      // Hide install prompt
      showInstallPrompt.value = false;
      // Log app installed
      console.log('PWA was installed');
    };
    
    const installPWA = async () => {
      if (!deferredPrompt.value) {
        return;
      }
      
      // Show the install prompt
      deferredPrompt.value.prompt();
      
      // Wait for the user to respond to the prompt
      const choiceResult = await deferredPrompt.value.userChoice;
      
      // User accepted the install prompt
      if (choiceResult.outcome === 'accepted') {
        console.log('User accepted the PWA installation');
      } else {
        console.log('User dismissed the PWA installation');
      }
      
      // Clear the deferredPrompt
      deferredPrompt.value = null;
      showInstallPrompt.value = false;
    };
    
    const dismissInstallPrompt = () => {
      showInstallPrompt.value = false;

      // Store in localStorage that user dismissed the prompt
      // to avoid showing it again too soon
      localStorage.setItem('pwa-install-prompt-dismissed', Date.now().toString());
    };

    // Handle service worker updates - auto-reload when new version available
    const handleServiceWorkerUpdate = (event: any) => {
      console.log('New version available, reloading in 3 seconds...');

      toast.add({
        severity: 'info',
        summary: 'Mise à jour disponible',
        detail: 'Une nouvelle version est disponible. Rechargement automatique...',
        life: 3000
      });

      // Auto-reload after 3 seconds
      setTimeout(() => {
        const registration = event.detail;
        if (registration && registration.waiting) {
          // Tell the waiting service worker to activate
          registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        }
        // Reload the page
        window.location.reload();
      }, 3000);
    };

    onMounted(() => {
      // Check if the user has already dismissed the prompt recently
      const dismissedTime = localStorage.getItem('pwa-install-prompt-dismissed');
      const showPrompt = !dismissedTime || (Date.now() - parseInt(dismissedTime)) > 7 * 24 * 60 * 60 * 1000; // 7 days
      
      if (showPrompt) {
        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      }
      
      window.addEventListener('appinstalled', handleAppInstalled);

      // Listen for service worker updates
      window.addEventListener('sw-updated', handleServiceWorkerUpdate as EventListener);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.removeEventListener('sw-updated', handleServiceWorkerUpdate as EventListener);
    });
    
    return {
      showInstallPrompt,
      installPWA,
      dismissInstallPrompt
    };
  }
});
</script>

<style>
#app {
  font-family: 'Avenir', Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  height: 100vh;
  /* Fix pour Safari iOS - empêche le rubber band scrolling */
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
}

html, body {
  margin: 0;
  padding: 0;
  height: 100%;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

:root {
  --primary-color: #E94E1B;
  --secondary-color: #333333;
  --background-color: #f5f5f5;
  --text-color: #333333;
}

.pwa-install-prompt {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: #fff;
  z-index: 1000;
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
  padding: 1rem;
}

.pwa-prompt-content {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.pwa-buttons {
  display: flex;
  gap: 0.5rem;
}

.install-button {
  background-color: var(--primary-color);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
}

.dismiss-button {
  background-color: transparent;
  color: var(--text-color);
  border: 1px solid #ccc;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
}

@media (display-mode: standalone) {
  .pwa-install-prompt {
    display: none;
  }
}
</style>